import ViewportMotionGuard from "../components/ViewportMotionGuard.jsx";
import FuturesWheelReader from "../components/FuturesWheelReader.jsx";
import MapViewportControls from "../components/MapViewportControls.jsx";
import { fetchPublicLinkStatuses } from "../utils/publicGallery.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, { useNodesInitialized } from "reactflow";
import "reactflow/dist/style.css";
import "../embed/embed.css";
import CustomOffsetEdge from "../components/CustomOffsetEdge";
import PublicNode from "../components/PublicNode";
import ThemeGrid from "../components/ThemeGrid.jsx";
import { mapPresentationGraph } from "../utils/nodeHierarchy.js";
import { assignEdgeColors } from "../utils/edgeUtils";
import {
  calculateAutomaticEmbedHeight,
  calculateAspectFittedEmbedHeight,
  calculateEmbedFitPadding,
  calculateMapVisualBounds,
} from "./embedFit";
import { getEmbedThemeCssVariables } from "./embedTheme.js";
import { ensureEmbedFontLoaded, getEmbedFontCssVariables, normalizeEmbedFont } from "./embedFont.js";
import { normalizeEdgeRoutingMode } from "../utils/customEdgeGeometry.js";
import { applyContextualVisibility } from "../utils/relationshipVisibility.js";
import { applyPublicHoverPresentation } from "../utils/publicHoverPresentation.js";
import { getMapTheme, getThemeCssVariables, normalizeCanvasStyle } from "../utils/mapThemes.js";
import { ensureMapFontLoaded, getMapFontCssVariables, normalizeMapFont } from "../utils/mapFonts.js";
import { normalizeHierarchyEmphasis } from "../utils/hierarchyEmphasis.js";
import {
  KNOWMAPPED_WATERMARK_LINK,
  KNOWMAPPED_WATERMARK_WIDTH,
  normalizeWatermarkOpacity,
  watermarkForBackground,
} from "../utils/knowmappedWatermark.js";
import {
  shouldDisablePublicOpacityTransitions,
  shouldUseClickOnlyPublicFocus,
  requiresWindowsStableRendering,
  stabilizePublicNodePresentation,
} from "../utils/publicNodePresentation.js";
import { shouldRenderOnlyVisibleMapElements } from "../utils/mapPerformance.js";
import { publicationEnvelopeToEmbed } from "./publicationPayload.js";

const edgeTypes = { customOffset: CustomOffsetEdge };
const nodeTypes = { public: PublicNode };

function FitAfterNodeMeasurement({ fitMap }) {
  const nodesInitialized = useNodesInitialized();

  useEffect(() => {
    if (!nodesInitialized) return undefined;
    const animationFrame = window.requestAnimationFrame(fitMap);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [nodesInitialized, fitMap]);

  return null;
}

function normalizePayload(payload) {
  if (payload?.schema !== "knowledge-map-embed" || payload?.schema_version !== 1 || !payload?.map) {
    throw new Error("Unsupported knowledge-map response");
  }

  const hierarchyEmphasis = normalizeHierarchyEmphasis(payload.map.presentation?.hierarchy_emphasis);
  const nodes = (payload.map.nodes || []).map((node) => ({
    id: String(node.id),
    type: "public",
    position: node.position || { x: 0, y: 0 },
    style: {
      pointerEvents: "all",
      cursor: (Boolean(node.description) || (node.resources || []).length > 0) ? "pointer" : "default",
    },
    data: {
      label: node.label || "",
      description: node.description || "",
      is_core: Boolean(node.is_core),
      importance: node.importance || (node.is_core ? "core" : "supporting"),
      resources: node.resources || [],
      icon: node.icon || "none",
      semantic_data: node.semantic_data || {},
      hierarchy_emphasis: hierarchyEmphasis,
      theme_id: payload.map.presentation?.theme || "default",
    },
    draggable: false,
    selectable: false,
  }));
  const grouped = mapPresentationGraph(nodes, payload.map.edges || []);
  const nodeIds = new Set(grouped.nodes.map((node) => node.id));
  const presentation = payload.map.presentation || {};
  const edges = assignEdgeColors((payload.map.edges || [])
    .map((edge) => ({
      id: String(edge.id),
      source: String(edge.source),
      target: String(edge.target),
      label: edge.label || "",
      type: "customOffset",
      data: { routing: normalizeEdgeRoutingMode(edge.style?.routing) },
      animated: Boolean(edge.style?.animated),
      style: {
        stroke: edge.style?.color || "#666666",
        strokeWidth: Number(edge.style?.width) || 4.5,
        strokeDasharray: edge.style?.dash || "0",
        strength: edge.style?.strength || "inherit",
      },
      markerEnd: {
        type: "arrowclosed",
        color: edge.style?.color || "#666666",
        width: Math.max(8, Math.min(12, Math.round((Number(edge.style?.width) || 4.5) * 2))),
        height: Math.max(8, Math.min(12, Math.round((Number(edge.style?.width) || 4.5) * 2))),
      },
    }))
    .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)),
    presentation.theme || "default",
    presentation.canvas_style || "theme",
    presentation.relationship_weight || "standard",
    presentation.theme_tokens
  );

  return { metadata: payload.map, nodes: grouped.nodes, edges };
}

/** Adapt an authenticated map record to the same contract used by public embeds. */
function normalizeMapPreviewRecord(record) {
  const mapId = String(record?.id || record?.map_id || "").trim();
  if (!mapId || !Array.isArray(record?.nodes) || !Array.isArray(record?.edges)) {
    throw new Error("The selected map could not be prepared for preview.");
  }
  const snapshot = {
    schema_version: 1,
    id: mapId,
    label: record.label || record.title || "Untitled map",
    graph_type: record.graph_type || "concept",
    color_mode: record.color_mode || "default",
    layout_type: record.layout_type || "smart",
    view_settings: record.view_settings || {},
    nodes: record.nodes,
    edges: record.edges,
  };
  return normalizePayload(publicationEnvelopeToEmbed({
    schema_version: 1,
    map_id: mapId,
    published_revision: 1,
    content_hash: "theme-inspector-preview",
    watermark_required: false,
    snapshot,
  }));
}

export default function KnowledgeMapEmbed({
  instanceId,
  mapId,
  apiBase,
  publicMapBase,
  publicViewerBase = "https://knowmapped.com",
  fontBaseUrl,
  showTitle,
  showFrame,
  showBackground,
  widthMode,
  heightMode,
  explorationEnabled,
  loadDefaultFont = false,
  previewMap = null,
  themeOverride = null,
  canvasStyleOverride = null,
}) {
  const [state, setState] = useState(() => previewMap
    ? { status: "ready", map: previewMap, error: null }
    : { status: "loading", map: null, error: null });
  const [coarsePointer, setCoarsePointer] = useState(
    () => window.matchMedia?.("(pointer: coarse)").matches ?? false
  );
  const [interactionEnabled, setInteractionEnabled] = useState(
    () => explorationEnabled && !(window.matchMedia?.("(pointer: coarse)").matches ?? false)
  );
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [publicMapLinkStates, setPublicMapLinkStates] = useState({});
  const frameRef = useRef(null);
  const flowInstanceRef = useRef(null);
  const initialFitCompleteRef = useRef(false);
  const userInteractedRef = useRef(false);
  const endpoint = useMemo(
    () => `${String(publicMapBase).replace(/\/+$/, "")}/maps/${encodeURIComponent(mapId)}.json`,
    [mapId, publicMapBase]
  );
  const serializedFont = state.map?.metadata?.presentation?.font;
  const embedFont = useMemo(() => normalizeEmbedFont(serializedFont), [serializedFont]);
  const fontFamily = embedFont?.id || normalizeMapFont(state.map?.metadata?.presentation?.font_family);
  const contextualGraph = useMemo(() => {
    if (!state.map) return { nodes: [], edges: [] };
    return applyContextualVisibility(state.map.nodes, state.map.edges, {
      selectedNodeId,
      focusMode: state.map.metadata.presentation?.focus_mode || "disabled",
      declutterStrength: state.map.metadata.presentation?.declutter_strength ?? 65,
      showRelationshipLabels: state.map.metadata.presentation?.show_relationship_labels !== false,
    });
  }, [state.map, selectedNodeId]);
  const contextualNodes = contextualGraph.nodes;
  const visibleEdges = contextualGraph.edges;
  const publicMapLinkTargetIds = useMemo(() => Array.from(new Set(
    (state.map?.nodes || [])
      .map((node) => node.data?.semantic_data?.map_link?.target_map_id)
      .filter(Boolean)
      .map(String)
  )).sort(), [state.map]);
  const applyHoverFocus = useCallback(({ nodeId = null, edgeId = null } = {}) => {
    if (!state.map) return;
    const hoverGraph = applyContextualVisibility(state.map.nodes, state.map.edges, {
      focusedNodeId: nodeId,
      focusedEdgeId: edgeId,
      selectedNodeId,
      focusMode: state.map.metadata.presentation?.focus_mode || "disabled",
      declutterStrength: state.map.metadata.presentation?.declutter_strength ?? 65,
      showRelationshipLabels: state.map.metadata.presentation?.show_relationship_labels !== false,
    });
    applyPublicHoverPresentation(frameRef.current, hoverGraph);
  }, [state.map, selectedNodeId]);
  const restoreBasePresentation = useCallback(() => {
    applyPublicHoverPresentation(frameRef.current, contextualGraph);
  }, [contextualGraph]);
  const fitMap = useCallback(() => {
    const canvas = frameRef.current?.querySelector(".knowledge-map-embed__canvas");
    if (!canvas || !flowInstanceRef.current || userInteractedRef.current) return false;
    const { width, height } = canvas.getBoundingClientRect();
    if (width <= 0 || height <= 0) return false;
    const padding = calculateEmbedFitPadding({
      width,
      height,
      showFrame,
      showBackground,
      widthMode,
    });
    const host = frameRef.current?.parentElement;
    if (heightMode === "auto" && host) {
      const baselineHeight = calculateAutomaticEmbedHeight({
        width,
        viewportHeight: window.innerHeight,
        widthMode,
      });
      const measuredNodes = flowInstanceRef.current.getNodes?.() || [];
      const visualBounds = calculateMapVisualBounds(measuredNodes, []);
      const titleHeight = Math.max(0, frameRef.current.getBoundingClientRect().height - height);
      const automaticHeight = calculateAspectFittedEmbedHeight({
        width,
        visualBounds,
        titleHeight,
        minimumHeight: baselineHeight,
      });
      if (
        automaticHeight &&
        Math.abs(host.getBoundingClientRect().height - automaticHeight) > 1
      ) {
        host.style.height = `${automaticHeight}px`;
        return false;
      }
    }
    // Match the established Publii renderer: width modes control the canvas,
    // and React Flow fits the node footprint inside it. Including routed edge
    // geometry here can greatly overstate dense-map bounds and make a wide map
    // appear as a small centered island.
    flowInstanceRef.current.fitView({ padding, duration: 0 });
    frameRef.current?.classList.add("knowledge-map-embed__frame--ready");
    initialFitCompleteRef.current = true;
    return true;
  }, [showFrame, showBackground, widthMode, heightMode]);

  const fitMapFromControls = useCallback(() => {
    const canvas = frameRef.current?.querySelector(".knowledge-map-embed__canvas");
    const instance = flowInstanceRef.current;
    if (!canvas || !instance) return false;
    const { width, height } = canvas.getBoundingClientRect();
    if (width <= 0 || height <= 0) return false;
    const padding = calculateEmbedFitPadding({
      width,
      height,
      showFrame,
      showBackground,
      widthMode,
    });
    return instance.fitView({ padding, duration: 180 });
  }, [showFrame, showBackground, widthMode]);

  useEffect(() => {
    if (!state.map) return;
    const fontBase = String(fontBaseUrl).replace(/\/+$/, "");
    const load = embedFont
      ? ensureEmbedFontLoaded(embedFont, fontBase)
      : ensureMapFontLoaded(fontFamily, fontBase, { loadDefault: loadDefaultFont });
    load.then(fitMap).catch(() => {});
  }, [apiBase, embedFont, fitMap, fontBaseUrl, fontFamily, loadDefaultFont, state.map]);

  useEffect(() => {
    if (previewMap) {
      setState({ status: "ready", map: previewMap, error: null });
      return undefined;
    }
    const controller = new AbortController();
    initialFitCompleteRef.current = false;
    userInteractedRef.current = false;
    flowInstanceRef.current = null;
    setState({ status: "loading", map: null, error: null });

    fetch(endpoint, { signal: controller.signal, credentials: "omit" })
      .then((response) => {
        if (!response.ok) throw new Error(response.status === 404 ? "Map not found or private" : "Map could not be loaded");
        return response.json();
      })
      .then((payload) => setState({ status: "ready", map: normalizePayload(publicationEnvelopeToEmbed(payload)), error: null }))
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({ status: "error", map: null, error: error.message });
        }
      });

    return () => controller.abort();
  }, [endpoint, previewMap]);

  useEffect(() => {
    const media = window.matchMedia?.("(pointer: coarse)");
    if (!media) return undefined;

    const handleChange = (event) => {
      setCoarsePointer(event.matches);
      setInteractionEnabled(explorationEnabled && !event.matches);
    };
    media.addEventListener?.("change", handleChange);
    return () => media.removeEventListener?.("change", handleChange);
  }, [explorationEnabled]);

  useEffect(() => {
    setInteractionEnabled(explorationEnabled && !coarsePointer);
  }, [coarsePointer, explorationEnabled]);

  useEffect(() => {
    if (!publicMapLinkTargetIds.length) {
      setPublicMapLinkStates({});
      return undefined;
    }
    let stopped = false;
    let controller = null;
    const refresh = async () => {
      if (stopped || document.hidden) return;
      controller?.abort();
      controller = new AbortController();
      try {
        const batches = [];
        for (let index = 0; index < publicMapLinkTargetIds.length; index += 250) {
          batches.push(publicMapLinkTargetIds.slice(index, index + 250));
        }
        const apiRoot = `${String(apiBase).replace(/\/+$/, "")}/api`;
        const bodies = publicMapBase
          ? [{ items: await fetchPublicLinkStatuses(publicMapLinkTargetIds, `${String(publicViewerBase).replace(/\/+$/, "")}/public-map-status`, { signal: controller.signal }) }]
          : await Promise.all(batches.map(async (targetIds) => {
          const response = await fetch(`${apiRoot}/map-links/resolve-public`, {
            method: "POST",
            cache: "no-store",
            credentials: "omit",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ target_ids: targetIds }),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`Link resolution failed: ${response.status}`);
          return response.json();
        }));
        if (!stopped) setPublicMapLinkStates(Object.fromEntries(
          bodies.flatMap((body) => body.items || []).map((item) => [String(item.target_map_id), item])
        ));
      } catch (error) {
        if (error.name !== "AbortError" && !stopped) {
          setPublicMapLinkStates(Object.fromEntries(
            publicMapLinkTargetIds.map((targetId) => [targetId, { state: "restricted_or_unavailable" }])
          ));
        }
      }
    };
    const handleVisibilityChange = () => { if (!document.hidden) refresh(); };
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stopped = true;
      controller?.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [apiBase, publicMapBase, publicViewerBase, publicMapLinkTargetIds]);

  useEffect(() => {
    if (!coarsePointer || !interactionEnabled) return undefined;

    const relock = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "pointerdown" && frameRef.current?.contains(event.target)) return;
      if (event.type === "visibilitychange" && document.visibilityState === "visible") return;
      setInteractionEnabled(false);
    };

    document.addEventListener("pointerdown", relock, true);
    document.addEventListener("keydown", relock);
    document.addEventListener("visibilitychange", relock);
    return () => {
      document.removeEventListener("pointerdown", relock, true);
      document.removeEventListener("keydown", relock);
      document.removeEventListener("visibilitychange", relock);
    };
  }, [coarsePointer, interactionEnabled]);

  useEffect(() => {
    const canvas = frameRef.current?.querySelector(".knowledge-map-embed__canvas");
    if (!canvas || !window.ResizeObserver) return undefined;

    let animationFrame;
    const observer = new ResizeObserver(() => {
      if (userInteractedRef.current) return;
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(fitMap);
    });
    observer.observe(canvas);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [state.status, fitMap]);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(restoreBasePresentation);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [restoreBasePresentation]);

  useEffect(() => {
    let animationFrame;
    const handleViewportResize = () => {
      if (userInteractedRef.current) return;
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(fitMap);
    };
    window.addEventListener("resize", handleViewportResize);
    window.addEventListener("load", handleViewportResize);
    if (document.readyState === "complete") handleViewportResize();
    return () => {
      window.removeEventListener("resize", handleViewportResize);
      window.removeEventListener("load", handleViewportResize);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [fitMap]);

  if (state.status === "loading") {
    return <p className="knowledge-map-embed__status" role="status">Loading knowledge map…</p>;
  }

  if (state.status === "error") {
    return (
      <div className="knowledge-map-embed__error" role="alert">
        <p>{state.error}</p>
        <a href={`${String(publicViewerBase).replace(/\/+$/, "")}/view/${encodeURIComponent(mapId)}`}>
          Open the map directly
        </a>
      </div>
    );
  }

  const { metadata } = state.map;
  const activeThemeId = themeOverride || metadata.presentation?.theme;
  const activeCanvasStyle = canvasStyleOverride || normalizeCanvasStyle(activeThemeId, metadata.presentation?.canvas_style);
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const windowsStableRendering = requiresWindowsStableRendering(userAgent);
  const baseTheme = getMapTheme(
    activeThemeId,
    activeCanvasStyle
  );
  const theme = {
    ...baseTheme,
    gridStyle: { ...baseTheme.gridStyle, ...(!themeOverride ? (metadata.presentation?.theme_tokens?.gridStyle || {}) : {}) },
  };
  const themeVariables = {
    ...getThemeCssVariables(
      activeThemeId,
      activeCanvasStyle
    ),
    ...getEmbedThemeCssVariables(themeOverride ? null : metadata.presentation?.theme_tokens),
  };
  const backgroundColor = themeVariables["--map-background"] || theme.background;
  const gridColor = themeVariables["--map-grid"] || theme.grid;
  const textColor = themeVariables["--map-text"] || theme.text;
  const disableOpacityTransitions = shouldDisablePublicOpacityTransitions(
    state.map.nodes,
    state.map.edges,
    metadata.presentation?.focus_mode || "disabled",
    userAgent
  );
  const clickOnlyFocus = shouldUseClickOnlyPublicFocus({
    userAgent,
    nodes: state.map.nodes,
    edges: state.map.edges,
    focusMode: metadata.presentation?.focus_mode || "disabled",
  });
  const visibleNodes = contextualNodes.map((node) => stabilizePublicNodePresentation({
    ...node,
    data: {
      ...node.data,
      theme_id: activeThemeId,
      tooltipOpen: node.id === selectedNodeId,
      onDetailsVisibilityChange: (open) => setSelectedNodeId(open ? node.id : null),
      disableOpacityTransition: disableOpacityTransitions,
      mapLinkContext: {
        publicStatusBase: publicMapBase ? `${String(publicViewerBase).replace(/\/+$/, "")}/public-map-status` : null,
        apiBase: `${String(apiBase).replace(/\/+$/, "")}/api`,
        appBase: String(apiBase).replace(/\/+$/, ""),
        publicViewerBase: String(publicViewerBase).replace(/\/+$/, ""),
        embedded: true,
        publicState: publicMapLinkStates[String(node.data?.semantic_data?.map_link?.target_map_id)]?.state || "checking",
      },
    },
  }));
  const themedVisibleEdges = themeOverride
    ? assignEdgeColors(visibleEdges, activeThemeId, activeCanvasStyle, metadata.presentation?.relationship_weight || "standard")
    : visibleEdges;
  const stableVisibleEdges = themedVisibleEdges.map((edge) => ({
    ...edge,
    data: {
      ...edge.data,
      disableOpacityTransition: disableOpacityTransitions,
      disablePointerInteraction: windowsStableRendering,
    },
  }));
  return (
    <div
      data-graph-type={metadata.graph_type}
      className={`knowledge-map-embed__frame${metadata.graph_type === "futures_wheel" ? " knowledge-map-embed__frame--ready" : ""}${windowsStableRendering ? " knowledge-map-embed__frame--windows-stable" : ""}`}
      ref={frameRef}
      style={{
        ...themeVariables,
        ...getMapFontCssVariables(fontFamily),
        ...getEmbedFontCssVariables(embedFont),
        background: showBackground ? backgroundColor : "transparent",
      }}
    >
      {showTitle && (
        <h2
          className={showBackground ? "knowledge-map-embed__title" : "knowledge-map-embed__native-title"}
          data-knowledge-map-title="true"
          style={showBackground ? { color: textColor } : undefined}
        >
          {metadata.title}
        </h2>
      )}
      <div
        className={`knowledge-map-embed__canvas${!interactionEnabled ? " knowledge-map-embed__canvas--locked" : ""}`}
        aria-label={`Knowledge map: ${metadata.title}`}
        onSelectStart={(event) => event.preventDefault()}
        onWheelCapture={(event) => {
          if (metadata.graph_type !== "futures_wheel" && event.ctrlKey) event.stopPropagation();
        }}
      >
        {explorationEnabled && coarsePointer && (
          <div className="knowledge-map-embed__actions">
            <button
              type="button"
              className="knowledge-map-embed__action"
              aria-pressed={interactionEnabled}
              onClick={() => setInteractionEnabled((enabled) => !enabled)}
            >
              {interactionEnabled ? "Done" : "Explore map"}
            </button>
            {!interactionEnabled && (
              <a
                className="knowledge-map-embed__open-link"
                href={`${String(publicViewerBase).replace(/\/+$/, "")}/view/${encodeURIComponent(mapId)}`}
              >
                Open full map
              </a>
            )}
          </div>
        )}
        {metadata.graph_type === "futures_wheel" ? <FuturesWheelReader data={{ ...metadata, label: metadata.title, color_mode: activeThemeId, view_settings: { ...metadata.view_settings, canvas_style: activeCanvasStyle }, nodes: metadata.nodes, edges: metadata.edges }} fontBaseUrl={fontBaseUrl} interactive={interactionEnabled} background={showBackground ? backgroundColor : "transparent"}/> : <ReactFlow
          id={instanceId}
          nodes={visibleNodes}
          edges={stableVisibleEdges}
          onlyRenderVisibleElements={shouldRenderOnlyVisibleMapElements(visibleNodes.length, stableVisibleEdges.length)}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.06 }}
          minZoom={0.08}
          maxZoom={2}
          nodesDraggable={false}
          nodesConnectable={false}
          nodesFocusable={false}
          edgesFocusable={false}
          elementsSelectable={false}
          panOnDrag={interactionEnabled}
          zoomOnScroll={interactionEnabled}
          zoomOnPinch={false}
          zoomOnDoubleClick={interactionEnabled}
          preventScrolling={interactionEnabled}
          onNodeMouseEnter={clickOnlyFocus ? undefined : (_, node) =>
            applyHoverFocus({ nodeId: node.id })
          }
          onNodeMouseLeave={clickOnlyFocus ? undefined : () =>
            restoreBasePresentation()
          }
          onEdgeMouseEnter={clickOnlyFocus ? undefined : (_, edge) =>
            applyHoverFocus({ edgeId: edge.id })
          }
          onEdgeMouseLeave={clickOnlyFocus ? undefined : () =>
            restoreBasePresentation()
          }
          onPaneClick={() => {
            setSelectedNodeId(null);
          }}
          onMoveStart={(event) => {
            if (event && initialFitCompleteRef.current) userInteractedRef.current = true;
          }}
          onInit={(instance) => {
            flowInstanceRef.current = instance;
            window.requestAnimationFrame(fitMap);
          }}
          proOptions={{ hideAttribution: true }}
        >
          <FitAfterNodeMeasurement fitMap={fitMap} />
          {showBackground && (
            <ThemeGrid
              theme={theme}
              gridMode={metadata.presentation?.grid_mode}
              showGrid={metadata.presentation?.show_grid !== false}
              color={gridColor}
            />
          )}
          {interactionEnabled && (
            <MapViewportControls
              onZoomIn={() => flowInstanceRef.current?.zoomIn({ duration: 150 })}
              onZoomOut={() => flowInstanceRef.current?.zoomOut({ duration: 150 })}
              onFit={fitMapFromControls}
            />
          )}
        <ViewportMotionGuard />
            </ReactFlow>}
        {metadata.watermark_required && (
          <a
            className="knowledge-map-embed__watermark"
            href={KNOWMAPPED_WATERMARK_LINK}
            target="_blank"
            rel="noreferrer"
            aria-label="Visit KnowMapped"
          >
            <img
              src={watermarkForBackground(theme.background).asset}
              alt=""
              aria-hidden="true"
              style={{ width: `${KNOWMAPPED_WATERMARK_WIDTH}px`, opacity: normalizeWatermarkOpacity(metadata.watermark_opacity) }}
            />
          </a>
        )}
      </div>
    </div>
  );
}

export { normalizePayload, normalizeMapPreviewRecord };
